import React, { useEffect } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useSaveDnmRoleMappings } from "~/entity/DnmRoleMappings";
import { DnmRoleMappingIProps } from "~/entity/DnmRoleMappings/model/schema";
import { useGetLocationData } from "~/hooks/locationData";
import { OpenModal } from "~/pages/Editor/type";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";

interface IDnmRoleMappingFormValues {
  dealerColumn: string;
  descriptionInDnm: string;
  csvColumn: string;
  descriptionInPd: string;
}

interface DnmRoleMappingFormProps {
  refetch: () => void;
  closeModal: () => void;
  currentData?: DnmRoleMappingIProps[];
  formId: string;
}

// Оставляем только поля схемы: createdAt/updatedAt нельзя отправлять в PUT
const toPayloadItem = (
  mapping: DnmRoleMappingIProps,
): DnmRoleMappingIProps => ({
  id: mapping.id,
  dealerColumn: mapping.dealerColumn,
  descriptionInDnm: mapping.descriptionInDnm,
  csvColumn: mapping.csvColumn,
  descriptionInPd: mapping.descriptionInPd,
  rolesId: mapping.rolesId ?? [],
  dealersId: mapping.dealersId ?? [],
});

const DnmRoleMappingForm = ({
  refetch,
  closeModal,
  currentData,
  formId,
}: DnmRoleMappingFormProps) => {
  const { t } = useTranslation();
  const { save } = useSaveDnmRoleMappings();
  const { notifySuccess, notifyError } = useNotify();
  const { currentId, openModal } = useGetLocationData();
  const isEdit = openModal === OpenModal.Edit;

  const currentMapping = isEdit
    ? currentData?.find((item) => item.id === Number(currentId))
    : undefined;

  const schema = yup.object().shape({
    dealerColumn: yup
      .string()
      .required(t("validation.required"))
      .max(255, t("validation.more255")),
    descriptionInDnm: yup
      .string()
      .required(t("validation.required"))
      .max(255, t("validation.more255")),
    csvColumn: yup
      .string()
      .required(t("validation.required"))
      .max(255, t("validation.more255")),
    descriptionInPd: yup
      .string()
      .required(t("validation.required"))
      .max(255, t("validation.more255")),
  });

  const { control, handleSubmit, reset, errors } =
    useForm<IDnmRoleMappingFormValues>({
      defaultValues: {
        dealerColumn: currentMapping?.dealerColumn ?? "",
        descriptionInDnm: currentMapping?.descriptionInDnm ?? "",
        csvColumn: currentMapping?.csvColumn ?? "",
        descriptionInPd: currentMapping?.descriptionInPd ?? "",
      },
      resolver: yupResolver(schema),
    });

  useEffect(() => {
    reset({
      dealerColumn: currentMapping?.dealerColumn ?? "",
      descriptionInDnm: currentMapping?.descriptionInDnm ?? "",
      csvColumn: currentMapping?.csvColumn ?? "",
      descriptionInPd: currentMapping?.descriptionInPd ?? "",
    });
  }, [currentMapping, reset]);

  const onError = (error: any) => {
    notifyError(error.message);
  };

  const onSubmit = (prop: IDnmRoleMappingFormValues) => {
    const payloadItems = (currentData ?? []).map(toPayloadItem);
    if (isEdit && currentMapping) {
      const updatedMappings = payloadItems.map((item) =>
        item.id === Number(currentId)
          ? {
              ...item,
              dealerColumn: prop.dealerColumn,
              descriptionInDnm: prop.descriptionInDnm,
              csvColumn: prop.csvColumn,
              descriptionInPd: prop.descriptionInPd,
            }
          : item,
      );
      save(
        { mappings: updatedMappings },
        {
          onSuccess: () => {
            notifySuccess(t("dnmRoleMappings.editedMapping"));
            refetch();
            closeModal();
          },
          onError,
        },
      );
    } else {
      const newMapping: DnmRoleMappingIProps = {
        dealerColumn: prop.dealerColumn,
        descriptionInDnm: prop.descriptionInDnm,
        csvColumn: prop.csvColumn,
        descriptionInPd: prop.descriptionInPd,
        rolesId: [],
        dealersId: [],
      };
      save(
        { mappings: [...payloadItems, newMapping] },
        {
          onSuccess: () => {
            notifySuccess(t("dnmRoleMappings.createdMapping"));
            refetch();
            closeModal();
          },
          onError,
        },
      );
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="input"
          name="dealerColumn"
          control={control}
          label={t("dnmRoleMappings.form.dealerColumn")}
          error={errors["dealerColumn"]?.message}
        />
        <Field
          type="input"
          name="descriptionInDnm"
          control={control}
          label={t("dnmRoleMappings.form.descriptionInDnm")}
          error={errors["descriptionInDnm"]?.message}
        />
        <Field
          type="input"
          name="csvColumn"
          control={control}
          label={t("dnmRoleMappings.form.csvColumn")}
          error={errors["csvColumn"]?.message}
        />
        <Field
          type="input"
          name="descriptionInPd"
          control={control}
          label={t("dnmRoleMappings.form.descriptionInPd")}
          error={errors["descriptionInPd"]?.message}
        />
      </Flex>
    </form>
  );
};

export default DnmRoleMappingForm;
